/**
 * The one slice stream a session may have running, so anything that ends the
 * session's claim on the patient's data can stop it without holding a
 * reference to the viewer's React tree.
 *
 * Module state, not Recoil, for the same reason `dicomIngestOwnership` is:
 * unloading a patient has to stop in-flight retrieval synchronously, within
 * the tick that tears the aggregate down — a Recoil round trip would let one
 * more slice land in a volume that no longer belongs to anyone.
 */

import type { SliceStreamController } from './SliceStreamController';

let active: SliceStreamController | null = null;

/**
 * Registers a controller as the session's stream, aborting whatever was
 * running before it. Two streams would be two writers into the same volumes.
 */
export function setActiveSliceStream(controller: SliceStreamController): void {
    active?.abort();
    active = controller;
}

/** Aborts the running stream, if any. Safe to call at any time, repeatedly. */
export function abortActiveSliceStream(): void {
    active?.abort();
    active = null;
}

/**
 * Clears the registration if `controller` is still the active one. Used by the
 * owner's teardown so a later stream registered by a newer viewer is not
 * dropped by an older one's cleanup.
 */
export function clearActiveSliceStream(
    controller: SliceStreamController
): void {
    if (active === controller) {
        active = null;
    }
}
